import { createSelector } from 'reselect';

import i18n from '../../config/i18n';
import { timeAgoOrDate } from '../../lib/date-formatter';
import { hashVal } from '../../lib/array-utils';
import type { RootPatronState } from '../../common/use-patron-selector';
import { programSelectors } from '../program';
import * as helpers from './helpers';

interface ContentIdParam {
  id?: number | undefined;
  contentId?: number | undefined;
}

const allContents = (state: RootPatronState) => state.contents;
const getContentId = (_: unknown, props: ContentIdParam) =>
  props.id || props.contentId || -1;

export const getContentById = createSelector(
  [allContents, getContentId],
  (contents, id) => contents[id]
);

export const getSafeContentById = createSelector(
  [getContentById],
  (content) => content || {}
);

export const getContentType = createSelector(
  [getSafeContentById],
  (content) => content.content_type
);

export const getContentIsPublished = createSelector(
  [getSafeContentById],
  (content) => content.publication_state === 'published'
);

export const getContentIsPending = createSelector(
  [getSafeContentById],
  (content) => content.publication_state === 'for_review'
);

export const getContentIsArchived = createSelector(
  [getSafeContentById],
  (content) => content.publication_state === 'archived'
);

export const getContentIsProcessing = createSelector(
  [getSafeContentById],
  (content) => content.processing_state !== 'completed'
);

export const getContentTags = createSelector(
  [getSafeContentById],
  (content) => content.buttons || []
);

export const getContentTitle = createSelector(
  [getSafeContentById],
  (content) => content.title
);

export const getContentSummary = createSelector(
  [getSafeContentById],
  (content) => content.description
);

export const getContentBodyExcerpt = createSelector(
  [getSafeContentById],
  (content) => content.body_excerpt
);

//applies the logic for deriving what to display for titling a piece of content
export const getContentFunctionalTitle = createSelector(
  [getContentTitle, getContentSummary],
  (title, summary) => title || summary
);

export const getContentTitleOrSummary = createSelector(
  [getContentTitle, getContentSummary],
  (title, summary) => title || summary || i18n.t('content.no_description')
);

export const getContentImages = createSelector(
  [getSafeContentById],
  (content) => content.shareable_image_urls && content.shareable_image_urls
);

export const getContentImageUrls = createSelector(
  [getSafeContentById],
  (content) =>
    content.shareable_image_urls &&
    content.shareable_image_urls.map((img) => img.url)
);

export const getContentImageUrl = createSelector(
  [getSafeContentById, getContentImageUrls],
  (content, imageUrls) =>
    imageUrls ? imageUrls[0] : content.shareable_image_url
);

export const getContentImageAlt = createSelector(
  [getSafeContentById],
  (content) => content.source_image_alt
);

export const getContentThumbnailUrls = createSelector(
  [getSafeContentById],
  (content) => {
    if (content.background_image_urls && content.background_image_urls.length) {
      return content.background_image_urls.map((img) => img.url);
    } else if (
      content.feed_preview_image_urls &&
      content.feed_preview_image_urls.length
    ) {
      return content.feed_preview_image_urls;
    }
    return undefined;
  }
);

export const getContentThumbnailUrl = createSelector(
  [getSafeContentById, getContentThumbnailUrls],
  (content, imageUrls) =>
    imageUrls ? imageUrls[0] : content.background_image_url
);

export const getContentExternalUrl = createSelector(
  [getSafeContentById],
  (content) =>
    content.external_content_url?.length ? content.external_content_url : null
);

export const getContentInternalUrl = createSelector(
  [getSafeContentById],
  (content) =>
    content.internal_content_url ? content.internal_content_url : null
);

export const getContentPublishedAt = createSelector(
  [getSafeContentById, getContentIsPublished],
  (content, isPublished) => isPublished && content.published_at
);

export const getContentReadTime = createSelector(
  [getSafeContentById, getContentIsPublished],
  (content, isPublished) => isPublished && content.read_time_in_seconds
);

export const getContentUuid = createSelector(
  [getSafeContentById],
  (content) => content.uuid
);

export const getContentPublishedDate = createSelector(
  [getContentPublishedAt],
  (publishedAt) => publishedAt && timeAgoOrDate(publishedAt)
);

export const getContentLiked = createSelector(
  [getSafeContentById],
  (content) => content.liked
);

export const getContentLikeCount = createSelector(
  [getSafeContentById],
  (content) => (content.reaction_counts && content.reaction_counts.likes) || 0
);

export const getContentReactionCounts = createSelector(
  [getSafeContentById],
  (content) => content.reaction_counts || {}
);

export const getContentReactions = createSelector(
  [getSafeContentById],
  (content) => content.expanded_reactions || []
);

export const getContentViewCount = createSelector(
  [getSafeContentById],
  (content) => (content.reaction_counts && content.reaction_counts.views) || 0
);

export const getContentBookmarked = createSelector(
  [getSafeContentById],
  (content) => content.bookmarked
);

export const getContentCommentCount = createSelector(
  [getSafeContentById],
  (content) => content.comment_count || 0
);

export const getContentShared = createSelector(
  [getSafeContentById],
  (content) =>
    content
      ? Boolean(
          content.shared ||
            content?.shared_to_social_networks?.length ||
            content?.scheduled_engagements?.length
        )
      : false
);

export const getContentIsUserSubmitted = createSelector(
  [getSafeContentById],
  helpers.getContentIsUserSubmitted
);

export const getContentAuthorId = createSelector(
  [getSafeContentById],
  (content) => content.author_id
);

export const getContentAuthorName = createSelector(
  [getSafeContentById],
  helpers.getContentAuthorName
);

export const getContentAuthorAvatarSrc = createSelector(
  [getSafeContentById],
  helpers.getContentAuthorAvatarSrc
);

export const getContentAuthorAvatarColor = createSelector(
  [getSafeContentById],
  helpers.getContentAuthorAvatarColor
);

export const getContentIsLink = createSelector(
  [getContentType],
  (contentType) => contentType === 'link'
);

export const getContentIsImage = createSelector(
  [getContentType],
  (contentType) => contentType === 'image'
);

export const getContentIsVideo = createSelector(
  [getContentType],
  (contentType) => contentType === 'video'
);

export const getContentIsNote = createSelector(
  [getContentType],
  (contentType) => contentType === 'note'
);

export const getContentIsArticle = createSelector(
  [getContentType],
  (contentType) => contentType === 'article'
);

export const getContentIsPortrait = createSelector(
  [getSafeContentById],
  (content) => content.orientation === 'portrait'
);

export const getContentIsSquare = createSelector(
  [getSafeContentById],
  (content) => content.orientation === 'square'
);

export const getContentIsMultiImage = createSelector(
  [getContentImageUrls, getContentIsImage],
  (imageUrls: string[] | false, isImage: boolean) =>
    imageUrls && imageUrls.length > 1 && isImage
);

export const getContentIsExternal = createSelector(
  [getContentIsNote, getContentInternalUrl],
  (isNote, url) => !(isNote || url)
);

export const getContentVideoEmbed = createSelector(
  [getSafeContentById, getContentIsVideo, getContentIsExternal],
  (content, isVideo) => (isVideo ? content?.video?.embed_html : null)
);

export const getContentVideoUuid = createSelector(
  [getSafeContentById, getContentIsVideo],
  (content, isVideo) => (isVideo ? content?.video?.uuid : null)
);

export const getContentDetailUrl = createSelector(
  [getContentId],
  helpers.getContentDetailPath
);

export const getContentBorderColor = createSelector(
  [getSafeContentById],
  (content) => content.border_color
);

export const getContentIsHtmlNote = createSelector(
  [getSafeContentById, getContentIsNote],
  (content, isNote) => isNote && content?.note_settings?.text_format === 'html'
);

export const getContentIsCompleted = createSelector(
  [
    getSafeContentById,
    getContentIsArticle,
    getContentIsLink,
    getContentIsMultiImage,
    getContentIsVideo,
  ],
  (content, isArticle, isLink, isImage, isVideo) => {
    if (isArticle || isLink || isImage || isVideo)
      return !!content.is_completed;
    else return true;
  }
);

// TODO: Fill out content.note_settings type
export const getContentNoteSettings = createSelector(
  [getSafeContentById, getContentIsNote],
  (content, isNote): any => (isNote && content.note_settings) || {}
);

export const getContentTitleOrSummaryPlain = createSelector(
  [getContentId, getContentIsHtmlNote, getContentTitleOrSummary],
  (content, isHtmlNote, titleOrSummary) => {
    return isHtmlNote
      ? new DOMParser().parseFromString(titleOrSummary, 'text/html').body
          .textContent || ''
      : titleOrSummary;
  }
);

export const getContentChannels = createSelector(
  [getSafeContentById],
  (content) => content.content_channels
);

export const getContentChannelIds = createSelector(
  [getSafeContentById],
  (content) => content.content_channel_ids
);

export const getContentPermalinkUrl = createSelector(
  [getSafeContentById],
  (content) => content.permalink_url
);

export const getContentSourceName = createSelector(
  [getSafeContentById],
  (content) => content.source_name
);

export const getContentSourceAvatar = createSelector(
  [getSafeContentById],
  (content) => content.source_avatar
);

export const getContentShareText = createSelector(
  [getSafeContentById, getContentTitle],
  (content, title) => content.prepopulated_text || title
);

export const getContentShareHashtag = createSelector(
  [getSafeContentById],
  (content) => content.appended_hashtag
);

export const getContentCanLike = createSelector(
  [getContentIsPublished],
  (isPublished) => isPublished
);

export const getContentCanBookmark = createSelector(
  [getContentIsPublished],
  (isPublished) => isPublished
);

export const getContentCanComment = createSelector(
  [
    getSafeContentById,
    getContentIsPublished,
    programSelectors.getProgramCanCommentContent,
  ],
  (content, isPublished, programCanComment) =>
    content.is_commentable && isPublished && programCanComment
);

export const getContentCanShare = createSelector(
  [getSafeContentById, getContentIsPublished],
  (content, isPublished) => content.can_share && isPublished
);

export const getContentCoverStyle = createSelector(
  [getSafeContentById],
  (content) => content.cover_style
);

export const getContentDisplayAsVideoOnly = createSelector(
  [getContentIsVideo, getContentCoverStyle],
  (isVideo, coverStyle) => isVideo && coverStyle !== 'video'
);

export const getContentHasVideo = createSelector(
  [getContentType, getContentCoverStyle],
  (contentType, coverStyle) => contentType === 'video' || coverStyle === 'video'
);

export const getContentCanEdit = createSelector(
  [getSafeContentById, getContentIsArchived, getContentIsProcessing],
  (content, isArchived, isProcessing) =>
    content.is_editable && !isArchived && !isProcessing
);

export const getContentCanTranslate = createSelector(
  [getSafeContentById, getContentIsPublished],
  (content, isPublished) => content.is_translatable && isPublished
);

export const getContentIsTranslated = createSelector(
  [getSafeContentById, getContentIsPublished],
  (content) => content.display_language === i18n.language
);

export const getContentLang = createSelector(
  [getSafeContentById],
  (content) => content.display_language || content.language
);

export const getContentAcknowledgement = createSelector(
  [getSafeContentById],
  (content) => content.acknowledgement
);

export const getContentUseCoverTextFromContent = createSelector(
  [getSafeContentById],
  (content) => content.use_cover_text_from_content
);

export const getContentCanAcknowledge = createSelector(
  [getContentAcknowledgement],
  (acknowledgement) => !!acknowledgement
);

export const getContentCanFlag = createSelector(
  [getSafeContentById],
  (content) => !!content.is_flaggable
);

// pdf_export_enabled is sent by pony's feed serializer (PR #6643).
// Requires content to be loaded (Boolean(content?.id) guard) so the action
// does not render prematurely when getSafeContentById falls back to {}.
// Defaults true when the field is absent on a loaded content.
export const getContentPdfExportEnabled = createSelector(
  [getSafeContentById],
  (content) => Boolean(content?.id) && content.pdf_export_enabled !== false
);

export const getContentDefaultImage = createSelector(
  [getContentUuid],
  (uuid) => {
    const fallbackImageUrls = [
      '/images/content_default0.png',
      '/images/content_default1.png',
      '/images/content_default2.png',
      '/images/content_default3.png',
    ];

    const fallbackIndex = hashVal(uuid || '', fallbackImageUrls.length);
    return fallbackImageUrls[fallbackIndex];
  }
);

export const getContentApprovalStatus = createSelector(
  [getContentById],
  (content) => content.approval_state
);

export const getContentIsUgcV2 = createSelector(
  [getSafeContentById],
  (content) => content.version === 2
);

export const getContentIsDraft = createSelector(
  [getSafeContentById],
  (content) => content.publication_state === 'draft'
);

export const getContentUpdatedAt = createSelector(
  [getSafeContentById],
  (content) => content.updated_at
);

export const getContentIsDuplicatable = createSelector(
  [getSafeContentById],
  (content) => !!content.is_duplicatable
);

// UGC v2 content that is not yet published and not archived (draft or for_review).
// Used to determine if the /preview route is needed instead of the standard detail route.
export const getContentIsNonPublishedUgcV2 = createSelector(
  [getContentIsUgcV2, getContentIsPublished, getContentIsArchived],
  (isUgcV2, isPublished, isArchived) => isUgcV2 && !isPublished && !isArchived
);

// UGC v2 content in archived state. Archived UGC cards are non-clickable.
export const getContentIsArchivedUgcV2 = createSelector(
  [getContentIsUgcV2, getContentIsArchived],
  (isUgcV2, isArchived) => isUgcV2 && isArchived
);

export default {
  getContentById,
  getContentType,
  getContentTags,
  getContentTitle,
  getContentFunctionalTitle,
  getContentSummary,
  getContentBodyExcerpt,
  getContentTitleOrSummary,
  getContentImages,
  getContentImageUrls,
  getContentImageUrl,
  getContentImageAlt,
  getContentThumbnailUrls,
  getContentThumbnailUrl,
  getContentExternalUrl,
  getContentInternalUrl,
  getContentDetailUrl,
  getContentVideoEmbed,
  getContentVideoUuid,
  getContentPublishedDate,
  getContentLiked,
  getContentLikeCount,
  getContentReactionCounts,
  getContentReactions,
  getContentViewCount,
  getContentBookmarked,
  getContentCommentCount,
  getContentShared,
  getContentAuthorId,
  getContentAuthorName,
  getContentAuthorAvatarSrc,
  getContentAuthorAvatarColor,
  getContentIsLink,
  getContentIsImage,
  getContentIsVideo,
  getContentIsNote,
  getContentIsArticle,
  getContentIsPortrait,
  getContentIsSquare,
  getContentIsMultiImage,
  getContentIsExternal,
  getContentIsPublished,
  getContentIsPending,
  getContentIsArchived,
  getContentIsProcessing,
  getContentIsHtmlNote,
  getContentIsCompleted,
  getContentTitleOrSummaryPlain,
  getContentBorderColor,
  getContentChannels,
  getContentChannelIds,
  getContentPermalinkUrl,
  getContentSourceName,
  getContentSourceAvatar,
  getContentShareText,
  getContentShareHashtag,
  getContentCanLike,
  getContentCanBookmark,
  getContentCanComment,
  getContentCanShare,
  getContentCanEdit,
  getContentCanTranslate,
  getContentCanFlag,
  getContentIsTranslated,
  getContentLang,
  getContentNoteSettings,
  getContentReadTime,
  getContentPublishedAt,
  getContentUuid,
  getContentAcknowledgement,
  getContentCanAcknowledge,
  getContentDefaultImage,
  getContentUseCoverTextFromContent,
  getContentApprovalStatus,
  getContentDisplayAsVideoOnly,
  getContentHasVideo,
  getContentIsUgcV2,
  getContentIsDraft,
  getContentUpdatedAt,
  getContentIsNonPublishedUgcV2,
  getContentIsArchivedUgcV2,
  getContentIsDuplicatable,
  getContentPdfExportEnabled,
};
